import React, { useEffect, useState, useContext } from "react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import styled from "styled-components";
import { SettingBody } from "../helpers/Admin_StyledComponents";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { EditReport } from "../Customers/Modal/EditReport";
import { DeleteReport } from "../Customers/Modal/DeleteReport";
import { NewReport } from "../Customers/Modal/NewReport";
import { UserContext } from "../../_context/UserContext";
import {
  SectionRibbon,
  SpacedGlassCard,
  SectionTitle,
  NoDataState,
  Loader,
} from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";

/** One source of truth for all field behaviors */
const DEFAULT_OPERATOR_OPTIONS = [
  { label: "less_than", value: "less_than" },
  { label: "greater_than", value: "greater_than" },
];

const FIELD_DEFS = [
  {
    label: "Helium Level: conditional",
    displayName: "Helium Level",
    value: "he_level_value",
    tooltip:
      "Receive helium level data less than or greater than a specified value.",
    needsOperator: true,
    hasThreshold: true,
    unit: "percent",
    operatorOptions: DEFAULT_OPERATOR_OPTIONS,
    threshold: { min: 0, max: 100, step: 0.1, default: 0 },
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "Helium Level",
    displayName: "All Helium Levels",
    value: "all_he_level",
    tooltip:
      "Receive helium level data for all selected systems. No conditions.",
    needsOperator: false,
    hasThreshold: false,
    unit: "percent",
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "Helium Pressure: conditional",
    displayName: "Helium Pressure",
    value: "he_pressure_value",
    tooltip:
      "Receive helium pressure data less than or greater than a specified value.",
    needsOperator: true,
    hasThreshold: true,
    unit: "psi/mbar",
    operatorOptions: DEFAULT_OPERATOR_OPTIONS,
    threshold: { min: 0, max: 180, step: 0.1, default: 0 },
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "Helium Pressure",
    displayName: "All Helium Pressures",
    value: "all_he_psi",
    tooltip:
      "Receive helium pressure data for all selected systems. No conditions.",
    needsOperator: false,
    hasThreshold: false,
    unit: "psi",
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "72-Hour Helium Pressure",
    displayName: "72-Hour Helium Pressure",
    value: "he_pressure_72_hr",
    tooltip: "Receive the last 72 hours of min/max helium pressure values.",
    needsOperator: false,
    hasThreshold: false,
    unit: "psi",
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "Scan Seconds",
    displayName: "Scan Seconds",
    value: "scan_seconds",
    tooltip: "Receive system and tube scan seconds. * GE CT & Siemens CT",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "Shield Temperature",
    displayName: "Shield Temperature",
    value: "shield_temp",
    tooltip: "Receive shield temperature data. * Siemens MRI",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "Disabled Alerts",
    displayName: "Disabled Alerts",
    value: "disabled_alerts",
    tooltip: "Report provides disabled user alerts.",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: false,
    admin: false,
    options_lists: true,
  },
  {
    label: "* Unsuccessful Acquisition - HHM",
    displayName: "Unsuccessful Acquisition - HHM",
    value: "unsuccessful_acqu_hhm",
    tooltip:
      "Report yields connection and file extraction errors. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Missed Stack Run - MMB",
    displayName: "Missed Stack Run - MMB",
    value: "missed_stack_run_mmb",
    tooltip:
      "Report yields MMB systems that are 1 stack run off. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Issue Tracker: All MMB",
    displayName: "Issue Tracker: All MMB",
    value: "mmb_all_issue_tracker",
    tooltip:
      "Report yields open issues associated with MMB. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Issue Tracker: All HHM",
    displayName: "Issue Tracker: All HHM",
    value: "hhm_all_issue_tracker",
    tooltip:
      "Report yields open issues associated with HHM. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Issue Tracker: Newer Than 30 Days - MMB",
    displayName: "Issue Tracker: Newer Than 30 Days - MMB",
    value: "mmb_issue_newer_30",
    tooltip:
      "Report yields open issues associated with MMB newer than 30 days. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Issue Tracker: Newer Than 30 Days - HHM",
    displayName: "Issue Tracker: Newer Than 30 Days - HHM",
    value: "hhm_issue_newer_30",
    tooltip:
      "Report yields open issues associated with HHM newer than 30 days. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Issue Tracker: Older Than 30 Days - HHM",
    displayName: "Issue Tracker: Older Than 30 Days - HHM",
    value: "hhm_issue_older_30",
    tooltip:
      "Report yields open issues associated with HHM older than 30 days. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
  {
    label: "* Issue Tracker: Older Than 30 Days - MMB",
    displayName: "Issue Tracker: Older Than 30 Days - MMB",
    value: "mmb_issue_older_30",
    tooltip:
      "Report yields open issues associated with MMB older than 30 days. * For Roles: superuser",
    needsOperator: false,
    hasThreshold: false,
    unit: null,
    superuser: true,
    admin: false,
    options_lists: false,
  },
];

function filter_by_super_role(options, roles) {
  const filtered_options = [];
  const isSuper = roles.includes("superuser");
  for (let opt of options) {
    if (opt.superuser === true && !isSuper) continue;
    filtered_options.push(opt);
  }
  return filtered_options;
}

const fieldDisplayNames = Object.fromEntries(
  FIELD_DEFS.map((f) => [
    f.value,
    f.displayName || f.label.replace(/:.*$/, ""),
  ]),
);

export const ReportSettingsFC = () => {
  const { userRoles } = useContext(UserContext);

  const [update, setUpdate] = useState(false);
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();

  const [reports, setReports] = useState([]);
  const [userSystemMetadata, setUserSystemMetadata] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsError, setReportErrors] = useState(false);

  // Remove FIELD_DEFS objects where superuser: true, but userRole excludes 'superuser'.
  const filtered_options = filter_by_super_role(FIELD_DEFS, userRoles);

  const getUserReports = async () => {
    setLoadingReports(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        method: "GET",
        url: `/user/reports/${id}`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });
      setReports(res.data[0]);
      setUserSystemMetadata(res.data[3]);
    } catch (error) {
      console.error(error);
      setReportErrors(true);
    }
    setLoadingReports(false);
  };

  useEffect(() => {
    getUserReports();
  }, [update]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SettingBody>
      <SectionRibbon title="Report Settings" />

      {loadingReports && !userSystemMetadata ? (
        <SpacedGlassCard>
          <LoadingContainer>
            <Loader size="lg" content="Loading report settings..." />
          </LoadingContainer>
        </SpacedGlassCard>
      ) : reportsError ? (
        <SpacedGlassCard>
          <NoDataState
            icon="⚠️"
            title="Unable to Load Reports"
            message="There was an error loading your report settings. Please try refreshing the page or contact support if the issue persists."
          />
        </SpacedGlassCard>
      ) : (
        <SpacedGlassCard>
          <SectionHeader>
            <SectionTitle>Your Reports</SectionTitle>
            <NewReport
              key="new"
              report={{
                id: null,
                author: id,
                report_name: null,
                systems_list: null,
                field_name: null,
                operator: null,
                threshold: null,
                email_schedule: null,
              }}
              machines={userSystemMetadata}
              update={update}
              setUpdate={setUpdate}
              field_name_tooltip={filtered_options}
            />
          </SectionHeader>

          {!loadingReports && (
            <>
              <TableHeader>
                <HeaderCell flex="2">Author</HeaderCell>
                <HeaderCell flex="2">System</HeaderCell>
                <HeaderCell flex="3">Report Name</HeaderCell>
                <HeaderCell flex="3">Field Name</HeaderCell>
                <HeaderCell flex="2">Operator</HeaderCell>
                <HeaderCell flex="1">Edit</HeaderCell>
                <HeaderCell flex="1">Remove</HeaderCell>
              </TableHeader>

              <TableBody>
                {reports.length === 0 ? (
                  <NoDataState
                    icon="📊"
                    title="No Reports Created Yet"
                    message="Start building your data insights! Create your first report to automatically receive system data via email on a schedule you choose."
                  />
                ) : (
                  reports.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell flex="2">{r.author}</TableCell>
                      <TableCell flex="2">
                        {r?.issue_system_id || "--"}
                      </TableCell>
                      <TableCell flex="3">{r.report_name}</TableCell>
                      <TableCell flex="3">
                        {fieldDisplayNames[r.field_name] || "--"}
                      </TableCell>
                      <TableCell flex="2">{r.operator || "--"}</TableCell>
                      <TableCell flex="1">
                        <EditReport
                          key={`edit-${r.id}`}
                          report={r}
                          machines={userSystemMetadata}
                          update={update}
                          setUpdate={setUpdate}
                          field_name_tooltip={filtered_options}
                        />
                      </TableCell>
                      <TableCell flex="1">
                        <DeleteReport
                          key={`del-${r.id}`}
                          report={r}
                          update={update}
                          setUpdate={setUpdate}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </>
          )}
        </SpacedGlassCard>
      )}
    </SettingBody>
  );
};

// Modern styled components for Report Settings
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
`;

const TableHeader = styled.div`
  display: flex;
  padding: 16px;
  background: ${colors.neutral[100]};
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.neutral[700]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const HeaderCell = styled.div`
  flex: ${(props) => props.flex};
  display: flex;
  align-items: center;
  padding: 0 8px;
`;

const TableBody = styled.div`
  border: 1px solid ${colors.neutral[200]};
  border-radius: 0 0 8px 8px;
  overflow: hidden;
`;

const TableRow = styled.div`
  display: flex;
  padding: 16px;
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TableCell = styled.div`
  flex: ${(props) => props.flex};
  display: flex;
  align-items: center;
  padding: 0 8px;
  font-size: 14px;
  color: ${colors.neutral[800]};

  /* Handle long text */
  word-break: break-word;
  min-width: 0;
`;
